// src/pages/Admin/Users/UserListing.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { FaPlus } from "react-icons/fa";
import Button from "./../../../components/Button/Button";
import ButtonEdit from "./../../../components/Button/ButtonEdit";
import ButtonDelete from "./../../../components/Button/ButtonDelete";
import { RiMailCheckFill } from "react-icons/ri";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import {
  getRoles,
  getAllUsers,
  getLatestUsers,
  deleteUser as deleteUserAPI,
  assignAdherentToUser,
  updateUserRole,
  toggleReceiveUserMessages,
} from "./../../../services/userServices";
import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "./../../Home/ScrollIndicator";
import AddUserModal from "./AddUserModal";
import EditUserModal from "./EditUserModal";
import ManageAdherentsModal from "./AdherentManagementModal";
import { ToastContainer, toast } from "react-toastify";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import "react-toastify/dist/ReactToastify.css";
import { toAbsoluteUserPhotoUrl } from "../../../services/userPhotoServices";

const sortIcon = (order) =>
  order === "none" ? "⇅" : order === "asc" ? "▲" : "▼";

export default function UserListing() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [users, setUsers] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roles, setRoles] = useState({});
  const [selectedRole, setSelectedRole] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showManageAdherentsModal, setShowManageAdherentsModal] =
    useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [editingRoleUserId, setEditingRoleUserId] = useState(null);

  // Tri “Créé le”
  const [createdSortOrder, setCreatedSortOrder] = useState("none");
  const toggleCreatedSort = () =>
    setCreatedSortOrder((o) =>
      o === "none" ? "asc" : o === "asc" ? "desc" : "none"
    );

  // Scroll perf (optionnel)
  const CHUNK = 60;
  const [visibleCount, setVisibleCount] = useState(CHUNK);
  const scrollRef = useRef(null);
  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
      setVisibleCount((v) =>
        v >= rows.length ? v : Math.min(v + CHUNK, rows.length)
      );
    }
  };

  const fetchData = async () => {
    try {
      const usersData = await getAllUsers();
      const rolesData = await getRoles();
      if (!Array.isArray(rolesData))
        throw new Error("rolesData should be an array");
      const rolesMap = rolesData.reduce(
        (acc, r) => ((acc[r.id] = r.nom), acc),
        {}
      );
      setUsers(usersData);
      setDisplayedUsers(usersData);
      setRoles(rolesMap);
    } catch (error) {
      console.error("Erreur chargement:", error);
      toast.error("Erreur lors du chargement des utilisateurs.");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filtres
  const filterUsers = (term, roleFilter = selectedRole) => {
    const t = (term || "").toLowerCase();
    const filtered = users.filter((u) => {
      const matchesName =
        (u.nom || "").toLowerCase().includes(t) ||
        (u.prenom || "").toLowerCase().includes(t);
      const matchesRole =
        roleFilter === "" || String(u.role_id) === String(roleFilter);
      return matchesName && matchesRole;
    });
    setDisplayedUsers(filtered);
    setVisibleCount(CHUNK);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    filterUsers(term, selectedRole);
  };

  // Tri appliqué à la liste affichée
  const rows = useMemo(() => {
    const arr = [...displayedUsers];
    if (createdSortOrder !== "none") {
      arr.sort((a, b) => {
        const da = new Date(
          a.createdOn || a.created_at || a.createdAt || 0
        ).getTime();
        const db = new Date(
          b.createdOn || b.created_at || b.createdAt || 0
        ).getTime();
        return createdSortOrder === "asc" ? da - db : db - da;
      });
    }
    return arr;
  }, [displayedUsers, createdSortOrder]);

  // Couleurs badges rôles (palette existante)
  const getRoleDisplay = (roleId) => {
    switch (roleId) {
      case 1:
        return { name: "Administrateur", className: "bg-red-500 text-white" };
      case 2:
        return { name: "Utilisateur", className: "bg-blue-500 text-white" };
      case 3:
        return { name: "Enseignant", className: "bg-green-500 text-white" };
      default:
        return { name: "Rôle inconnu", className: "bg-gray-500 text-white" };
    }
  };

  const getRecentUsers = async () => {
    try {
      const recent = await getLatestUsers();
      setDisplayedUsers(recent);
      setVisibleCount(CHUNK);
    } catch {
      toast.error("Erreur lors de la récupération des derniers utilisateurs");
    }
  };
  const getAllUsersAgain = () => setDisplayedUsers(users);

  const handleRoleChange = async (userId, newRoleId) => {
    try {
      const token =
        user?.token ||
        user?.accessToken ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      await updateUserRole(userId, parseInt(newRoleId, 10), token);

      const nextUsers = users.map((u) =>
        u.id === userId || u.user_id === userId
          ? { ...u, role_id: parseInt(newRoleId, 10) }
          : u
      );
      setUsers(nextUsers);

      // Refiltre selon recherche/role actif
      const term = (searchTerm || "").toLowerCase();
      const nextDisplayed = nextUsers.filter((u) => {
        const matchesName =
          (u.nom || "").toLowerCase().includes(term) ||
          (u.prenom || "").toLowerCase().includes(term);
        const matchesRole =
          selectedRole === "" || String(u.role_id) === String(selectedRole);
        return matchesName && matchesRole;
      });
      setDisplayedUsers(nextDisplayed);
      setEditingRoleUserId(null);
      toast.success("Rôle mis à jour !");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la mise à jour du rôle.");
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm(`Supprimer cet utilisateur ? ID: ${userId}`)) return;
    try {
      const msg = await deleteUserAPI(userId);
      toast.success(msg);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      setDisplayedUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression de l'utilisateur");
    }
  };

  const handleToggleReceiveMessages = async (userId, current) => {
    try {
      const token =
        user?.token ||
        user?.accessToken ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const updated = await toggleReceiveUserMessages(userId, !current, token);

      const newValue =
        typeof updated?.receive_messages !== "undefined"
          ? !!updated.receive_messages
          : !current;

      setUsers((prev) =>
        prev.map((x) =>
          (x.id ?? x.user_id) === userId
            ? { ...x, receive_messages: newValue }
            : x
        )
      );
      setDisplayedUsers((prev) =>
        prev.map((x) =>
          (x.id ?? x.user_id) === userId
            ? { ...x, receive_messages: newValue }
            : x
        )
      );

      toast.success(
        newValue
          ? "Réception des messages activée."
          : "Réception des messages désactivée."
      );
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du changement de réception des messages.");
    }
  };

  const handleAddUser = () => setShowModal(true);
  const openEditModal = (userId) => {
    setEditUserId(userId);
    setShowEditModal(true);
  };
  const openManageAdherentsModal = (userId) => {
    setEditUserId(userId);
    setShowManageAdherentsModal(true);
  };

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />
      <ScrollIndicator />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Administrateur"
          title="Listing Utilisateurs"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau dégradé + actions */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">
                Aide : Gestion des utilisateurs
              </h2>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
                🟨 Vous pouvez changer de rôle d'un utilisateur, en
                cliquant sur son rôle actuel.
              </p>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
                🟨 Vous pouvez attribuer un adhérent à un utilisateur en cliquant sur le bouton{" "}
                <span className="font-semibold">+/−</span>.
              </p>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
                🟨 Dans la colonne actions, l'icone{" "}
                en forme d'enveloppe indique si l'enseignant ou l'administrateur peut recevoir des messages des adhérents. Cliquer dessus pour activer ou
                désactiver la réception des messages.
              </p>

              {/* Actions */}
              <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                <Button
                  text="Ajouter Utilisateur"
                  onClick={handleAddUser}
                  icon={FaPlus}
                  className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-2"
                />
                <Button
                  text="Afficher les derniers inscrits"
                  onClick={getRecentUsers}
                  className="!bg-white/90 !text-slate-900 !hover:bg-white !rounded-xl !shadow-md"
                />
                <Button
                  text="Afficher Tous"
                  onClick={getAllUsersAgain}
                  className="!bg-white/90 !text-slate-900 !hover:bg-white !rounded-xl !shadow-md"
                />
              </div>
            </div>
          </div>

          {/* Filtres */}
          <div className="px-5 py-5 sm:px-8">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <input
                type="text"
                placeholder="Rechercher (nom, prénom)…"
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
              <select
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                value={selectedRole}
                onChange={(e) => {
                  const role = e.target.value;
                  setSelectedRole(role);
                  filterUsers(searchTerm, role);
                }}
              >
                <option value="">Tous les rôles</option>
                {Object.entries(roles).map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            {/* Compteur filtrés */}
            <div className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">
              Nombre d'utilisateurs affichés : {rows.length}
            </div>
          </div>

          {/* Tableau avec header + 2 premières colonnes collantes & scroll performant */}
          <div className="px-5 pb-6 sm:px-8">
            <div
              ref={scrollRef}
              onScroll={onScroll}
              className="overflow-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 max-h-[72vh]"
            >
              <table className="min-w-[1100px] w-full">
                <thead className="sticky top-0 z-20">
                  <tr className="bg-slate-50/90 dark:bg-slate-900/90 backdrop-blur uppercase tracking-wide text-xs">
                    {[
                      "Photo",
                      "Nom Prénom",
                      "Email",
                      "Rôle",
                      "Créé le",
                      "Gestion Adhérent",
                      "Actions",
                    ].map((h, idx) => (
                      <th
                        key={h}
                        className={`px-4 py-3 text-left font-semibold text-slate-700 dark:text-white ${
                          idx === 1
                            ? "sticky left-20 z-20 bg-slate-50/90 dark:bg-slate-900/90 backdrop-blur"
                            : ""
                        }`}
                      >
                        {h}
                        {h === "Créé le" && (
                          <button
                            type="button"
                            onClick={toggleCreatedSort}
                            className="ml-2 text-xs"
                            title="Trier par date de création"
                          >
                            {sortIcon(createdSortOrder)}
                          </button>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                  {rows.slice(0, visibleCount).map((u) => (
                    <tr
                      key={u.user_id || u.id}
                      className="transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                    >
                      {/* Photo (colonne collante) */}
                      <td className="px-4 py-3 sticky left-0 z-20 bg-white/90 dark:bg-slate-900/40">
                        <img
                          className="rounded-full w-10 h-10 object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                          src={toAbsoluteUserPhotoUrl(
                            u.photoURL || "/img/user_avatar.jpg"
                          )}
                          alt="Avatar"
                        />
                      </td>

                      {/* Nom (colonne collante) */}
                      <td className="px-4 py-3 sticky left-20 z-10 bg-white/90 dark:bg-slate-900/40">
                        <div className="font-semibold text-slate-900 dark:text-slate-100">
                          {u.nom} {u.prenom}
                        </div>
                      </td>

                      <td className="px-4 py-3">{u.email || "—"}</td>

                      {/* Rôle (inline edit au clic) */}
                      <td className="px-4 py-3">
                        {editingRoleUserId === u.id ? (
                          <select
                            value={u.role_id}
                            onChange={(e) =>
                              handleRoleChange(u.id, e.target.value)
                            }
                            onBlur={() => setEditingRoleUserId(null)}
                            className="w-full rounded-sm border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-xs"
                            autoFocus
                          >
                            {Object.entries(roles).map(([id, name]) => (
                              <option key={id} value={id}>
                                {name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span
                            className={`inline-block px-3 py-1 whitespace-nowrap text-xs font-semibold rounded-sm cursor-pointer ${
                              getRoleDisplay(u.role_id).className
                            }`}
                            onClick={() => setEditingRoleUserId(u.id)}
                            title="Cliquer pour modifier"
                          >
                            {getRoleDisplay(u.role_id).name}
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-3">
                        {u.createdOn || u.created_at || u.createdAt
                          ? new Date(
                              u.createdOn || u.created_at || u.createdAt
                            ).toLocaleDateString("fr-FR")
                          : "—"}
                      </td>

                      <td className="px-4 py-3 text-xs">
                        {u.adherents && u.adherents.length > 0 ? (
                          u.adherents.map((a, idx) => (
                            <div key={`${u.user_id || u.id}-${idx}`}>
                              {a.nom} {a.prenom}
                            </div>
                          ))
                        ) : (
                          <div>Aucun</div>
                        )}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <ButtonEdit onClick={() => openEditModal(u.id)} />

                          {[1, 3].includes(Number(u.role_id)) && (
                            <button
                              type="button"
                              onClick={() =>
                                handleToggleReceiveMessages(
                                  u.id,
                                  !!u.receive_messages
                                )
                              }
                              className="w-8 h-8 rounded-md flex items-center justify-center ring-1 ring-slate-200 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                              title={
                                u.receive_messages
                                  ? "Désactiver la réception des messages"
                                  : "Activer la réception des messages"
                              }
                              aria-label={
                                u.receive_messages
                                  ? "Désactiver la réception des messages"
                                  : "Activer la réception des messages"
                              }
                            >
                              <RiMailCheckFill
                                className={
                                  u.receive_messages
                                    ? "text-green-500"
                                    : "text-slate-400"
                                }
                                size={18}
                              />
                            </button>
                          )}

                          <button
                            className="w-8 h-8 bg-green-500 text-white rounded-md shadow-sm transition duration-300 ease-in-out hover:bg-green-700 flex items-center justify-center"
                            title="Gérer Adhérents"
                            onClick={() => openManageAdherentsModal(u.id)}
                          >
                            +/-
                          </button>
                          <ButtonDelete onClick={() => deleteUser(u.id)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {visibleCount < rows.length && (
                <div className="p-3 text-center text-xs text-slate-500">
                  Affichage de {visibleCount} / {rows.length} — descendez pour
                  charger la suite…
                </div>
              )}
            </div>
          </div>
        </div>

        <ToastContainer />
      </div>

      {/* Modales */}
      <AddUserModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        refreshList={async () => {
          const usersData = await getAllUsers();
          setUsers(usersData);
          setDisplayedUsers(usersData);
        }}
      />
      <EditUserModal
        isOpen={showEditModal}
        userId={editUserId}
        onClose={() => setShowEditModal(false)}
        refreshList={fetchData}
      />
      <ManageAdherentsModal
        isOpen={showManageAdherentsModal}
        onClose={() => setShowManageAdherentsModal(false)}
        userId={editUserId}
        refresh={fetchData}
      />
    </section>
  );
}

// (Facultatif) assignAdherentToUser existe déjà si besoin ailleurs
